/**
 * Handles the state of a grid view.
 *
 * @author Marcel Werk
 * @copyright 2001-2025 WoltLab GmbH
 * @license GNU Lesser General Public License <http://opensource.org/licenses/lgpl-license.php>
 * @since 6.2
 */
define('WoltLabSuite/Core/Component/GridView/State',["require", "exports", "tslib", "./Filter", "./Selection", "./Sorting"], function (require, exports, tslib_1, Filter_1, Selection_1, Sorting_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.State = void 0;
    Filter_1 = tslib_1.__importDefault(Filter_1);
    Selection_1 = tslib_1.__importDefault(Selection_1);
    Sorting_1 = tslib_1.__importDefault(Sorting_1);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
    class State extends EventTarget {
        #baseUrl;
        #filter;
        #pagination;
        #selection;
        #sorting;
        #gridViewFooter;
        #lastUrl;
        #pageNo;
        constructor(gridId, table, pageNo, baseUrl, sortField, sortOrder, defaultSortField, defaultSortOrder) {
            super();
            this.#baseUrl = baseUrl;
            this.#pageNo = pageNo;
            this.#gridViewFooter = document.getElementById(`${gridId}_footer`);
            this.#pagination = document.getElementById(`${gridId}_pagination`);
            this.#pagination.addEventListener("switchPage", (event) => {
                void this.#switchPage(event.detail, 2 /* StateChangeCause.Pagination */);
            });
            this.#filter = new Filter_1.default(gridId);
            this.#filter.addEventListener("grid-view:change", () => {
                this.#switchPage(1, 0 /* StateChangeCause.Change */);
            });
            this.#sorting = new Sorting_1.default(table, sortField, sortOrder, defaultSortField, defaultSortOrder);
            this.#sorting.addEventListener("grid-view:change", () => {
                this.#switchPage(1, 0 /* StateChangeCause.Change */);
            });
            this.#selection = new Selection_1.default(gridId, table);
            this.#selection.addEventListener("grid-view:get-bulk-interactions", (event) => {
                this.dispatchEvent(new CustomEvent("grid-view:get-bulk-interactions", { detail: { objectIds: event.detail.objectIds } }));
            });
            this.#selection.addEventListener("grid-view:update-selection", () => {
                this.#updateGridViewFooter();
            });
            if (this.#baseUrl) {
                window.addEventListener("popstate", () => {
                    this.#handlePopState();
                });
            }
            this.#updateLastUrl();
            this.#updatePaginationUrl();
            this.#updateGridViewFooter();
        }
        getPageNo() {
            return this.#pageNo;
        }
        getSortField() {
            return this.#sorting.getSortField();
        }
        getSortOrder() {
            return this.#sorting.getSortOrder();
        }
        getActiveFilters() {
            return this.#filter.getActiveFilters();
        }
        getSelectedIds() {
            return this.#selection.getSelectedIds();
        }
        updateFromResponse(cause, count, filterLabels) {
            this.#filter.setFilterLabels(filterLabels);
            this.#pagination.count = count;
            this.#updatePaginationUrl();
            this.#selection.refresh();
            this.#updateLastUrl();
            if (cause === 0 /* StateChangeCause.Change */ || cause === 2 /* StateChangeCause.Pagination */) {
                this.#updateQueryString();
            }
            this.#updateGridViewFooter();
        }
        #switchPage(pageNo, source) {
            this.#pagination.page = pageNo;
            this.#pageNo = pageNo;
            this.dispatchEvent(new CustomEvent("grid-view:change", { detail: { source } }));
        }
        #updateQueryString() {
            if (!this.#baseUrl) {
                return;
            }
            const url = this.#getUrl();
            window.history.pushState({}, document.title, url.toString());
        }
        #updatePaginationUrl() {
            if (!this.#baseUrl) {
                return;
            }
            const url = this.#getUrl(false);
            this.#pagination.url = url.toString();
        }
        #getUrl(withPageNo = true) {
            const url = new URL(this.#baseUrl);
            const parameters = [];
            if (withPageNo && this.#pageNo > 1) {
                parameters.push(["pageNo", this.#pageNo.toString()]);
            }
            for (const parameter of this.#sorting.getQueryParameters()) {
                parameters.push(parameter);
            }
            for (const parameter of this.#filter.getQueryParameters()) {
                parameters.push(parameter);
            }
            if (parameters.length > 0) {
                url.search += url.search !== "" ? "&" : "?";
                url.search += new URLSearchParams(parameters).toString();
            }
            return url;
        }
        #handlePopState() {
            const url = new URL(window.location.href);
            if (this.#isOnlyHashChange(url)) {
                return;
            }
            let pageNo = 1;
            const value = url.searchParams.get("pageNo");
            if (value !== null) {
                pageNo = parseInt(value);
                if (Number.isNaN(pageNo) || pageNo < 1) {
                    pageNo = 1;
                }
            }
            this.#filter.updateFromSearchParams(url.searchParams);
            this.#sorting.updateFromSearchParams(url.searchParams);
            this.#switchPage(pageNo, 1 /* StateChangeCause.History */);
        }
        #updateLastUrl() {
            if (!this.#baseUrl) {
                return;
            }
            const url = this.#getUrl();
            this.#lastUrl = url.pathname + url.search;
        }
        #isOnlyHashChange(url) {
            return url.pathname + url.search === this.#lastUrl;
        }
        #updateGridViewFooter() {
            const hasPagination = this.#pagination.count > 1;
            this.#gridViewFooter.hidden = !hasPagination && !this.#selection.selectionBarVisible();
            const paginationContainer = this.#pagination.closest(".gridView__pagination");
            if (paginationContainer !== null) {
                paginationContainer.hidden = !hasPagination;
            }
        }
        setBulkInteractionContextMenuOptions(options) {
            this.#selection.setBulkInteractionContextMenuOptions(options);
        }
        resetSelection() {
            this.#selection.resetSelection();
        }
        removeSelection(objectId) {
            this.#selection.removeSelection(objectId);
        }
        refreshSelection() {
            this.#selection.refresh();
        }
    }
    exports.State = State;
    exports.default = State;
});

